import Permission from '@constants/permission'
import ProductSelector from '@components/shared/product-selector'
import React, { ReactElement, useEffect, useState } from 'react'
import Toolbar from '@components/shared/toolbar'
import hasPerms from '@utils/has-permission'
import { AppDispatch, FeaturedItemList } from '@types'
import {
  Card,
  CardContent,
  CardMedia,
  CircularProgress,
  Paper,
  Typography,
} from '@mui/material'
import { Save } from '@mui/icons-material'
import { getFeaturedItems, setFeaturedItems } from '@queries/featured'
import { makeStyles } from 'tss-react/mui'
import { setFlashMessage } from '@store/reducers/flash-message/actions'
import { useDispatch } from 'react-redux'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

const Featured = (): ReactElement => {
  const { classes } = useStyles()
  const canEdit = hasPerms(Permission.FEATURED_ITEMS_UPDATE)
  const dispatch = useDispatch<AppDispatch>()
  const queryClient = useQueryClient()
  const [items, setItems] = useState<FeaturedItemList>(emptySlots())

  const { isLoading, data: featuredItems } = useQuery({
    queryKey: ['featuredItems'],
    queryFn: getFeaturedItems,
  })

  useEffect(() => {
    if (!featuredItems) return

    setItems(
      emptySlots().map(
        (slot) =>
          featuredItems.find((item) => item.sort_order === slot.sort_order) ||
          slot,
      ),
    )
  }, [featuredItems])

  const { isPending, mutate } = useMutation({
    mutationFn: setFeaturedItems,
    onSuccess: () => {
      dispatch(setFlashMessage('Featured Items Updated', 'success'))
      queryClient.invalidateQueries({ queryKey: ['featuredItems'] })
    },
    onError: (error: Error) =>
      dispatch(setFlashMessage(error.message, 'error')),
  })

  const savedProductId = (sortOrder: number): number | undefined =>
    featuredItems?.find((item) => item.sort_order === sortOrder)?.product_id

  const handleProductChange = (sortOrder: number) => (productId: number) =>
    setItems((current) =>
      current.map((item) =>
        item.sort_order === sortOrder
          ? { ...item, product_id: productId }
          : item,
      ),
    )

  const handleSave = () =>
    mutate(
      items.map(({ product_id, sort_order }) => ({ product_id, sort_order })),
    )

  const hasEmptySlot = items.some((item) => item.product_id === -1)
  const isDirty = items.some(
    (item) => savedProductId(item.sort_order) !== item.product_id,
  )

  return (
    <Paper>
      <Toolbar
        buttons={[
          {
            disabled: !canEdit || isPending || hasEmptySlot || !isDirty,
            onClick: handleSave,
            startIcon: <Save />,
            title: 'Save',
            variant: 'contained',
          },
        ]}
        description='Pick the three products featured in the app landing page, in order from left to right. The center one is the highly prominent cup at the top when you hit the landing page.'
        title='Featured Items'
      />
      {isLoading ? (
        <div className={classes.loader}>
          <CircularProgress />
        </div>
      ) : (
        <div className={classes.slots}>
          {items.map((item) => {
            const isSavedProduct =
              item.product_id === savedProductId(item.sort_order)

            return (
              <Card
                className={classes.slot}
                key={item.sort_order}
                variant='outlined'
              >
                {item.image && isSavedProduct ? (
                  <CardMedia
                    alt={`${SLOT_LABELS[item.sort_order]} featured product`}
                    className={classes.image}
                    component='img'
                    image={item.image}
                  />
                ) : (
                  <div className={classes.placeholder}>
                    <Typography color='textSecondary' variant='body2'>
                      {item.product_id === -1
                        ? 'No product selected'
                        : 'Image appears here once this selection is saved'}
                    </Typography>
                  </div>
                )}
                <CardContent>
                  <Typography gutterBottom variant='subtitle1'>
                    {SLOT_LABELS[item.sort_order]}
                  </Typography>
                  <ProductSelector
                    containerProps={{ fullWidth: true }}
                    disabled={!canEdit || isPending}
                    onChange={handleProductChange(item.sort_order)}
                    value={item.product_id}
                  />
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}
    </Paper>
  )
}

export default Featured

/**
 * Slot position is the `sort_order` of the item: 0 left, 1 middle, 2 right.
 */
const SLOT_LABELS = ['Left', 'Middle', 'Right']

const emptySlots = (): FeaturedItemList =>
  SLOT_LABELS.map((_label, index) => ({
    product_id: -1,
    image: '',
    sort_order: index,
  }))

const useStyles = makeStyles()((theme) => ({
  slots: {
    display: 'flex',
    padding: theme.spacing(3),
    paddingTop: 0,
  },
  slot: {
    flex: 1,
    marginRight: theme.spacing(2),
    '&:last-child': {
      marginRight: 0,
    },
  },
  image: {
    height: 220,
    objectFit: 'contain',
    backgroundColor: theme.palette.action.hover,
  },
  placeholder: {
    alignItems: 'center',
    backgroundColor: theme.palette.action.hover,
    display: 'flex',
    height: 220,
    justifyContent: 'center',
    padding: theme.spacing(2),
    textAlign: 'center',
  },
  loader: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(5),
  },
}))
